import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { Scene } from "@babylonjs/core/scene";
import { Ship } from "../ship/Ship";
import type { ShipSpec } from "../config/ships";
import type { Track } from "../track/Track";
import { neutralControl, type ControlState } from "../input/types";

/** Per-bot driving personality. Combined with the random ship choice, this is
 * what gives each opponent a recognisable, signature feel over a race. */
export interface BotProfile {
  /** How far ahead it aims (fraction of lap): low = hugs the line, high = cuts
   * corners on a smooth racing line. */
  lookahead: number;
  /** Steering sharpness — high = darty/precise, low = lazy/wide. */
  steerGain: number;
  /** Preferred lane offset from centre (world units). */
  lane: number;
  /** Amplitude of a slow lane weave (world units) — sloppier bots wander. */
  weaveAmp: number;
  /** Weave frequency (Hz). */
  weaveFreq: number;
}

const rand = (a: number, b: number) => a + Math.random() * (b - a);

/** How close to an edge (world units) a bot drives before it shies away. */
const WALL_MARGIN = 10;
/** How hard it steers away, per unit inside that margin. */
const WALL_SHY = 0.15;

/** Roll a random signature profile within sensible ranges. */
export function randomBotProfile(halfWidth: number): BotProfile {
  const sloppy = Math.random() < 0.5;
  return {
    lookahead: rand(0.009, 0.022),
    steerGain: rand(1.8, 3.4),
    lane: rand(-1, 1) * (halfWidth - 8),
    weaveAmp: sloppy ? rand(3, 11) : 0,
    weaveFreq: rand(0.4, 1.4),
  };
}

/**
 * A basic AI opponent: a {@link Ship} steered to follow the racing line, with a
 * per-bot {@link BotProfile} so each one drives with its own character — how much
 * it cuts corners, how sharply it steers, and how much it wanders. Pace comes
 * from its (random) ship's real stats, same balance as the player.
 */
export class Bot {
  readonly ship: Ship;
  readonly name: string;
  readonly profile: BotProfile;
  private readonly ctrl: ControlState = neutralControl();
  private t = 0;

  constructor(scene: Scene, spec: ShipSpec, name: string, profile: BotProfile) {
    this.ship = new Ship(scene, spec);
    this.name = name;
    this.profile = profile;
  }

  placeAtStart(pos: Vector3, forward: Vector3): void {
    this.ship.placeAtStart(pos, forward);
  }

  update(dt: number, track: Track): void {
    this.t += dt;
    const p = this.profile;
    const lane = p.lane + p.weaveAmp * Math.sin(this.t * p.weaveFreq * Math.PI * 2);

    const ship = this.ship;
    const here = track.project(ship.position, ship.roadHint);
    const aim = track.pointAt(here.t + p.lookahead, lane);

    // Turn toward the aim point within the plane the craft is steering in (the
    // road surface, or level in the air): the signed angle from the heading.
    const up = ship.airborne ? Vector3.UpReadOnly : ship.up;
    const to = aim.subtract(ship.position);
    to.subtractInPlace(up.scale(Vector3.Dot(to, up)));
    const dYaw = Math.atan2(Vector3.Dot(Vector3.Cross(ship.heading, to), up), Vector3.Dot(ship.heading, to));

    // Keep off the walls. On the inside of a bend the aim point down the road
    // can lie across the wall from an outside lane; heading straight at it
    // pins the craft there, scrubbing speed every frame. So near an edge, steer
    // away from it, harder the closer it is.
    let steer = dYaw * p.steerGain;
    const margin = here.halfWidth - WALL_MARGIN;
    if (!here.closed && Math.abs(here.u) > margin) steer -= Math.sign(here.u) * (Math.abs(here.u) - margin) * WALL_SHY;

    this.ctrl.steer = Math.max(-1, Math.min(1, steer));
    this.ctrl.brake = 0;
    this.ctrl.boost = false;
    this.ctrl.pause = false;
    this.ship.update(dt, this.ctrl, track);
  }

  dispose(): void {
    this.ship.root.dispose();
  }
}
